(() => {
  "use strict";
  let state;
  let previewCards = [];
  function publishPreviewCards(cards) {
    // Retain the current nodes so a deferred preview applet can catch up
    // when the initial idle render precedes its event listener.
    previewCards = cards;
    document.dispatchEvent(new CustomEvent("swish:debut-cards", {detail: {cards}}));
  }
  const BLANK_IMAGE_BY_KEY = {
    "basic-tee": "https://cdn.shopify.com/s/files/1/0028/1852/files/basic-tee.webp",
    "crop-top": "https://cdn.shopify.com/s/files/1/0028/1852/files/swish-format-crop-top-male.jpg?v=1790522869",
  };
  const BASIC_TEE_PRINTFUL_IMAGE = "https://cdn.shopify.com/s/files/1/0028/1852/files/printful-blank-basic-tee.jpg?v=1790521763";
  const el = (tag, cls, text) => {
    const n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text != null) n.textContent = text;
    return n;
  };
  const money = (cents) =>
    new Intl.NumberFormat(state.config.locale || "en", {
      style: "currency",
      currency: state.config.currency || "USD",
    }).format(cents / 100);
  const picture = (src, alt, fallbackSrc) => {
    const img = el("img");
    // A missing optional placeholder is still a thumbnail target. This empty,
    // valid image contains no garment/art and stays invisible until the preview
    // applet has admitted and decoded this product's actual render.
    img.src = src || "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='300' height='300'/%3E";
    img.alt = src ? alt : "";
    if (!src) {
      img.dataset.swpEmpty = "true";
      img.style.visibility = "hidden";
    }
    img.loading = src ? "lazy" : "eager";
    img.width = 300;
    img.height = 300;
    if (fallbackSrc)
      img.addEventListener("error", () => {
        img.removeAttribute("srcset");
        img.src = fallbackSrc;
      }, { once: true });
    return img;
  };
  const button = (label, cls, fn) => {
    const b = el("button", cls, label);
    b.type = "button";
    b.addEventListener("click", fn);
    return b;
  };
  function finishedSelection(item, variant) {
    const p = item.product;
    if (!p || !variant || !Array.isArray(variant.options) || !Array.isArray(p.options) || variant.options.length !== p.options.length) return null;
    const selectedOptions = p.options.map((o, i) => ({name: typeof o === "string" ? o : o.name, value: variant.options[i]}));
    if (selectedOptions.some((o) => typeof o.name !== "string" || typeof o.value !== "string" || !o.value)) return null;
    return {productId: String(p.id), variantId: String(variant.id), selectedOptions,
      formatFamily: /coaster/i.test(item.v.key) ? "coaster" : /mug/i.test(item.v.key) ? "mug" : "apparel"};
  }
  function finishedPhoto(image, item, variant) {
    document.dispatchEvent(new CustomEvent("swish:finished-card", {detail: {image, selection: finishedSelection(item, variant)}}));
  }
  function associatedPhotoVariant(item) {
    const p = item.product;
    if (!p || !Array.isArray(p.variants)) return null;
    // An observed unique photo association, never the first array variant.
    const matches = p.variants.filter((v) => v.featured_image?.src && v.featured_image.src === p.featured_image);
    return matches.length === 1 ? matches[0] : null;
  }
  function swatchPaint(product, index, value) {
    const row = (state.config.swatchColors || "").split(/\r?\n/).find(line =>
      line.slice(0, line.indexOf(":")).trim().toLowerCase() === value.trim().toLowerCase());
    const definition = row ? row.slice(row.indexOf(":") + 1).trim() : "";
    if (/^(#[0-9a-f]{6}|#[0-9a-f]{3})+$/i.test(definition)) {
      const colors = definition.match(/#[0-9a-f]{6}|#[0-9a-f]{3}/gi);
      return colors.length === 1 ? colors[0] : "linear-gradient(135deg," + colors.map((c, i) =>
        c + " " + (i * 100 / colors.length) + "% " + ((i + 1) * 100 / colors.length) + "%").join(",") + ")";
    }
    const variant = product.variants.find(v => v.options[index] === value && v.featured_image?.src);
    return variant ? "url(" + JSON.stringify(variant.featured_image.src) + ") center / contain no-repeat #fff" : null;
  }
  function prioritizeBasic(items, region) {
    if (!region || region === "us") return items.slice();
    const result = items.slice();
    for (const kind of ["real", "debut"]) {
      const index = result.findIndex(i => i.kind === kind && i.v.key === "basic-tee" && (kind === "debut" || i.product?.available));
      if (index < 0) continue;
      const [basic] = result.splice(index, 1);
      const signature = kind === "real" ? result.findIndex(i => i.kind === kind && i.v.key === "t-shirt") : -1;
      const first = result.findIndex(i => i.kind === kind);
      result.splice(signature >= 0 ? signature + 1 : first >= 0 ? first : result.length, 0, basic);
    }
    return result;
  }
  const promoteBasic = item => state.region && state.region !== "us" && item.v.key === "basic-tee" && (item.kind === "debut" || item.product?.available);
  function sortSizes(values) {
    const rank = value => {
      const key = String(value).trim().toUpperCase().replace(/\s/g, "");
      const aliases = {XXXS:0,"3XS":0,XXS:1,"2XS":1,XS:2,S:3,M:4,L:5,XL:6,XXL:7,"2XL":7,XXXL:8,"3XL":8,"4XL":9,"5XL":10};
      if (key in aliases) return aliases[key];
      if (/^\d+XL$/.test(key)) return 99;
      const numeric = key.match(/^\d+(?:\.\d+)?/);
      return numeric ? 100 + Number(numeric[0]) : 99;
    };
    return values.slice().sort((a,b) => rank(a)-rank(b) || String(a).localeCompare(String(b), undefined, {numeric:true}));
  }
  function formatDifference(item) {
    const current = state.items.find(i => i.isCurrent && i.kind === "real");
    if (item.isCurrent || !current || (item.kind === "real" && !item.product.available)) return "";
    const amount = item.kind === "real" ? item.product.price : item.estimate?.cents;
    if (!Number.isFinite(amount)) return "";
    const delta = amount - current.product.price;
    if (!Number.isFinite(delta)) return "";
    return delta === 0 ? "Same starting price" : money(Math.abs(delta)) + (delta < 0 ? " less" : " more") + " · starting prices";
  }
  function estimateText(item) {
    if (!item.estimate) return "";
    const current = state.items.find(i => i.isCurrent && i.kind === "real");
    const delta = current ? item.estimate.cents - current.product.price : 0;
    const comparison = delta < 0 ? " · Save " + money(-delta) : delta > 0 ? " · " + money(delta) + " more" : "";
    return (item.v.key === "basic-tee" ? "" : "About ") + money(item.estimate.cents) + comparison;
  }
  const mount = (payload) => {
    if (state) return;
    const host = document.getElementById("swish-format-studio");
    const dialog = document.getElementById("swish-format-dialog");
    if (!host || !dialog) return;
    state = {
      ...payload,
      region: window.SwishMarketAvailability?.region,
      items: prioritizeBasic(payload.items, window.SwishMarketAvailability?.region),
      host,
      dialog,
      mode: "real",
      category: "all",
      selected: null,
    };
    state.l = payload.config.labels;
    document.body.append(dialog);
    const open = host.querySelector(".sw-studio__open");
    open.disabled = false;
    open.textContent = state.l.compare + " (" + payload.items.length + ")";
    const status = host.querySelector(".sw-studio__status");
    status.textContent = "";
    open.addEventListener("click", () => show());
    dialog
      .querySelector(".sw-format__close")
      .addEventListener("click", () => dialog.close());
    dialog.addEventListener("click", (e) => {
      if (e.target === dialog) {
        const r = dialog.getBoundingClientRect();
        if (
          e.clientX < r.left ||
          e.clientX > r.right ||
          e.clientY < r.top ||
          e.clientY > r.bottom
        )
          dialog.close();
      }
    });
    dialog.addEventListener("close", () => {
      const confirmation = document.getElementById("swish-debut-confirm-modal");
      if (confirmation && confirmation.style.display !== "none" && !confirmation.hidden) return;
      document.body.style.overflow = state.previousOverflow || "";
    });
    renderIdle();
    open.addEventListener("click", resetIdleCards, {capture: true});
    // Rebind the idle thumbnails after the picker releases its preview scope.
    dialog.addEventListener("close", () => {
      setTimeout(() => {
        if (dialog.open || !idleCards.length) return;
        // The closed picker will rebuild these nodes on its next open. Detach
        // them so the retired PR141 scope can release its thumbnail URLs.
        dialog.querySelectorAll(".sw-format__card img").forEach(image => image.remove());
        bindIdleCards();
      }, 0);
    });
    const styling = el("style");
    styling.textContent = `.sw-format__difference{display:block;font-size:12px;line-height:1.4;margin-top:5px}.sw-format__option-price{display:block;font-size:11px;line-height:1.3;margin-top:4px}.sw-format__option:has(.sw-format__option-price){min-width:68px;padding:10px 8px}.sw-format__card .sw-format__blank-image{display:block;width:100%;aspect-ratio:1;object-fit:contain;background:#fff;margin-bottom:8px}`;
    host.append(styling);
    renderModes();
  };
  let idleCards = [];
  function resetIdleCards() {
    // Detach the composed images before PR141 releases their scope so it can
    // revoke their object URLs. Fresh photo nodes are rebound after closing.
    for (const card of idleCards) {
      const image = picture(card.source, card.label);
      card.image.replaceWith(image);
      card.image = image;
    }
  }
  function bindIdleCards() {
    if (idleCards.length)
      publishPreviewCards(idleCards);
  }
  function renderIdle() {
    const real = state.items.filter(i => i.kind === "real" && !i.isCurrent);
    const debut = state.items.filter(i => i.kind === "debut");
    if (!real.length && !debut.length) return;
    const host = state.host;
    host.classList.add("sw-studio--styles");
    const heading = el("h2", "sw-studio__title", "This design, more styles");
    host.querySelector(".sw-studio__eyebrow").replaceWith(heading);
    const current = state.items.find(i => i.isCurrent);
    const subtitle = el("p", "sw-studio__subtitle",
      "You're viewing the " + (current?.v.label || state.current) + ". Explore " + (real.length + debut.length) + " other styles.");
    const summary = host.querySelector(".sw-studio__summary");
    summary.replaceWith(subtitle);
    const open = summary.querySelector(".sw-studio__open");
    open.setAttribute("aria-label", open.textContent + ". See all " + state.items.length);
    open.textContent = "See all " + state.items.length;
    const shortlist = host.querySelector(".sw-studio__shortlist");
    shortlist.replaceChildren();
    idleCards = [];
    for (const [kind, items, title] of [["real", real, "More styles"], ["debut", debut, "Be first to try this design on"]]) {
      if (!items.length) continue;
      const section = el("section", "sw-studio__row sw-studio__row--" + kind);
      const header = el("div", "sw-studio__row-heading");
      if (kind === "debut") header.append(el("span", "sw-studio__new", "New"));
      header.append(el("h3", "", title));
      const rail = el("div", "sw-studio__rail");
      rail.id = "sw-studio-" + kind + "-styles";
      const arrows = el("div", "sw-studio__arrows");
      for (const [direction, label, glyph] of [[-1, "Previous styles", "‹"], [1, "Next styles", "›"]]) {
        const arrow = button(glyph, "sw-studio__arrow", () => rail.scrollBy({
          left: direction * rail.clientWidth * .8,
          behavior: matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth",
        }));
        arrow.setAttribute("aria-label", label);
        arrow.setAttribute("aria-controls", rail.id);
        arrows.append(arrow);
      }
      header.append(arrows);
      section.append(header);
      if (kind === "debut") section.append(el("p", "sw-studio__row-subtitle", "Preview a new style, then choose your size. Printed only after you order."));
      for (const item of items) {
        const tile = button("", "sw-studio__mini", () => { resetIdleCards(); show(item); });
        const src = item.product?.featured_image || (item.v.key === "basic-tee" ? BASIC_TEE_PRINTFUL_IMAGE : item.v.blank_image || BLANK_IMAGE_BY_KEY[item.v.key]);
        if (src || kind === "debut") {
          const image = picture(src, item.v.label);
          tile.append(image);
          if (kind === "real") queueMicrotask(() => finishedPhoto(image, item, associatedPhotoVariant(item)));
          if (kind === "debut") idleCards.push({image, formatKey: item.v.key, label: item.v.label, source: src});
        }
        tile.append(el("strong", "", item.v.label), el("span", "", kind === "real"
          ? money(item.product.price)
          : item.estimate ? (item.v.key === "basic-tee" ? "" : "About ") + money(item.estimate.cents) : state.l.request_note));
        rail.append(tile);
      }
      section.append(rail);
      shortlist.append(section);
      const updateOverflow = () => { arrows.hidden = kind === "debut" && rail.scrollWidth <= rail.clientWidth; };
      if (typeof ResizeObserver !== "undefined") new ResizeObserver(updateOverflow).observe(rail);
      updateOverflow();
    }
    shortlist.after(open);
    bindIdleCards();
  }
  function show(item) {
    if (!state.dialog.open) {
      state.previousOverflow = document.body.style.overflow;
      state.dialog.showModal();
      document.body.style.overflow = "hidden";
    }
    state.mode = item?.kind || (state.items.some(i => i.kind === "real") ? "real" : "debut");
    state.category = "all";
    state.selected = item || null;
    renderModes();
    renderCards();
  }
  function renderModes() {
    const box = state.dialog.querySelector(".sw-format__modes");
    box.replaceChildren();
    for (const [kind, label] of [
      ["real", state.l.existing],
      ["debut", state.l.create],
    ]) {
      const count = state.items.filter((i) => i.kind === kind).length;
      if (!count) continue;
      const b = button(label + " (" + count + ")", "", () => {
        state.mode = kind;
        state.category = "all";
        state.selected = null;
        renderModes();
        renderCards();
      });
      b.setAttribute("aria-pressed", String(state.mode === kind));
      box.append(b);
    }
    state.dialog.querySelector(".sw-format__help").textContent =
      state.mode === "debut" ? state.l.create_help : state.l.same_design;
  }
  function renderCards() {
    const items = state.items.filter((i) => i.kind === state.mode);
    state.dialog.dataset.singleFormat = String(items.length === 1);
    const filters = state.dialog.querySelector(".sw-format__filters");
    filters.replaceChildren();
    for (const category of [
      "all",
      ...new Set(items.map((i) => i.v.category || state.l.all)),
    ]) {
      const b = button(category === "all" ? state.l.all : category, "", () => {
        state.category = category;
        state.selected = null;
        renderCards();
      });
      b.setAttribute("aria-pressed", String(category === state.category));
      filters.append(b);
    }
    const shown = items.filter(
      (i) =>
        state.category === "all" ||
        (i.v.category || state.l.all) === state.category,
    );
    const cards = state.dialog.querySelector(".sw-format__cards");
    cards.replaceChildren();
    if (!state.selected || !shown.includes(state.selected))
      state.selected = shown.find(i => i.isCurrent) || shown[0];
    const debutCards = [];
    for (const item of shown) {
      const b = button("", "sw-format__card", () => {
        state.selected = item;
        renderCards();
        if (matchMedia("(max-width:749px)").matches)
          state.dialog
            .querySelector(".sw-format__detail")
            .scrollIntoView({ block: "start", behavior: "auto" });
      });
      b.setAttribute("aria-pressed", String(item === state.selected));
      const isBasicTeeBlank = item.kind === "debut" && item.v.key === "basic-tee";
      const cardImage = item.product?.featured_image || (isBasicTeeBlank ? BASIC_TEE_PRINTFUL_IMAGE : item.v.blank_image || BLANK_IMAGE_BY_KEY[item.v.key]);
      if (cardImage || item.kind === "debut") {
        const image = picture(cardImage, item.v.label, isBasicTeeBlank ? BLANK_IMAGE_BY_KEY["basic-tee"] : null);
        if (item.kind === "debut") {
          image.className = "sw-format__blank-image";
          debutCards.push({image, formatKey: item.v.key, label: item.v.label});
        }
        b.append(image);
        if (item.kind === "real") {
          image.dataset.swishFinishedProduct = String(item.product.id);
          queueMicrotask(() => finishedPhoto(image, item, associatedPhotoVariant(item)));
        }
      }
      b.append(
        el("strong", "", item.v.label),
        el(
          "span",
          "",
          item.kind === "real"
            ? money(item.product.price)
            : item.estimate ? (item.v.key === "basic-tee" ? "" : "About ") + money(item.estimate.cents) : state.l.request_note,
        ),
      );
      if (formatDifference(item)) b.append(el("small", "sw-format__difference", formatDifference(item)));
      if (promoteBasic(item)) b.append(el("small", "sw-format__value-note", "Lower-price option"));
      if (item.kind === "real" && !item.product.available)
        b.append(el("small", "", state.l.sold_out));
      cards.append(b);
    }
    // The Debut preview applet may compose this product's artwork onto these
    // cards; an empty list releases any earlier cards.
    publishPreviewCards(debutCards);
    renderDetail(state.selected);
  }
  function renderDetail(item) {
    const panel = state.dialog.querySelector(".sw-format__detail");
    panel.replaceChildren();
    if (!item) {
      panel.append(el("p", "", state.l.no_match));
      return;
    }
    if (item.product?.featured_image)
      panel.append(picture(item.product.featured_image, item.v.label));
    panel.append(el("h3", "", item.v.label));
    if (item.v.blurb) panel.append(el("p", "", item.v.blurb));
    if (item.kind === "debut") {
      if (item.estimate) {
        panel.append(el("p", "sw-format__price", estimateText(item)));
        
      }
      panel.append(el("p", "", state.l.create_help));
      const previewMount = el("div", "sw-format__debut-preview");
      panel.append(previewMount);
      document.dispatchEvent(
        new CustomEvent("swish:debut-preview", {
          detail: { mount: previewMount, formatKey: item.v.key, label: item.v.label },
        }),
      );
      panel.append(
        button(state.l.new_format, "sw-format__submit", () => {
          state.dialog.close();
          state.create(item);
        }),
      );
      return;
    }
    const p = item.product,
      price = el(
        "p",
        "sw-format__price",
        p.price==null?"":(p.price_varies ? state.l.from + " " : "") + money(p.price),
      );
    panel.append(price);
    const api=window.SwishMarketAvailability;
    const details = el("a", "", state.l.details);
    details.href = item.url;
    if (!p.available)api.mount(panel,p);
    if (!p.available || p.variants.length >= 250 || p.requires_selling_plan) {
      panel.append(
        el("p", "", p.available ? state.l.not_loaded : state.l.sold_out),
        details,
      );
      return;
    }
    const form = el("form", "sw-format__form");
    form.action = (state.config.root || "/") + "cart/add";
    form.method = "post";
    const selectors = [], groups=[];let ui,submitting=false;
    p.options.forEach((option, index) => {
      const name = typeof option === "string" ? option : option.name;
      let values = Array.from(
        new Set(p.variants.map((v) => v.options[index])),
      );
      if (index === api.sizeIndex(p)) values = sortSizes(values);
      const group = el("fieldset", "sw-format__options");
      const legend = el("legend", "", name);
      group.append(legend);
      const isColor = /^(colou?r|couleur)$/i.test(name);
      const chosen = el("span", "sw-format__chosen");
      if (isColor) legend.append(chosen);
      const select = {value: values.length === 1 ? values[0] : "", options: []};
      values.forEach(value => {
        const op = button(value, "sw-format__option", () => {
          if(submitting)return;
          const productPhoto = panel.querySelector(":scope > img");
          if (productPhoto) finishedPhoto(productPhoto, item, null);
          select.value = select.value === value ? "" : value;
          const choices=selectors.map(s=>s.value),cleared=api.repair(p,choices,index);
          selectors.forEach(s => {
            if (s.options.length === 1) s.value = s.options[0].value;
          });
          if(cleared && selectors[api.sizeIndex(p)]?.options.length > 1)selectors[api.sizeIndex(p)].value='';
          ui?.selection(index,select.value,cleared);
          chosen.textContent = select.value ? " · " + select.value : "";
          if (isColor && select.value) {
            const image = p.variants.find(v => v.options[index] === select.value && v.featured_image?.src);
            if (image && panel.querySelector(":scope > img")) panel.querySelector(":scope > img").src = image.featured_image.src;
          }
          update(true);
        });
        const paint = isColor ? swatchPaint(p, index, value) : null;
        if (paint) {
          op.classList.add("sw-format__swatch");
          op.textContent = "";
          op.setAttribute("aria-label", value);
          op.title = value;
          const chip = el("span", "sw-format__chip");
          chip.setAttribute("aria-hidden", "true");
          chip.style.background = paint;
          op.append(chip);
        }
        op.value = value;
        op.setAttribute("aria-pressed", "false");
        select.options.push(op);
        group.append(op);
      });
      if (values.length === 1) {
        group.classList.add("sw-format__options--auto");
        group.hidden = true;
        chosen.textContent = " · " + select.value;
      }
      selectors.push(select);
      form.append(group);groups[index]=group;
    });
    api.order(p).forEach(i=>form.append(groups[i]));
    const id = el("input");
    id.type = "hidden";
    id.name = "id";
    form.append(id);
    const quantity = el("input");
    quantity.type = "hidden";
    quantity.name = "quantity";
    quantity.value = "1";
    form.append(quantity);
    const feedback = el("div", "sw-format__feedback");
    feedback.setAttribute("role", "status");
    const submit = el("button", "sw-format__submit", state.l.select_options);
    submit.type = "submit";
    submit.disabled = true;
    form.append(submit, feedback);
    panel.append(form, details);
    ui=api.mount(panel,p,()=>selectors.map(s=>s.value));
    function update(selectionChanged = false) {
      const choices = selectors.map((s) => s.value);
      selectors.forEach((select, index) =>
        Array.from(select.options).forEach((option) => {
          if (!option.value) return;
          option.setAttribute("aria-pressed", String(select.value === option.value));
          if (index === api.sizeIndex(p)) {
            option.querySelector(".sw-format__option-price")?.remove();
            const matches = p.variants.filter(v => v.available && choices.every((choice,i) => i === index || !choice || choice === v.options[i]));
            const prices = matches.filter(v => v.options[index] === option.value).map(v => v.price);
            const base = Math.min(...matches.map(v => v.price));
            const extra = Math.min(...prices) - base;
            if (prices.length && Number.isFinite(extra) && extra > 0) option.append(el("small", "sw-format__option-price", "+" + money(extra)));
            option.setAttribute("aria-label", option.value + (prices.length && Number.isFinite(extra) && extra > 0 ? ", +" + money(extra) : ""));
          }
          option.dataset.preferredSize=String(index===api.sizeIndex(p)&&api.preference()?.enabled&&api.preference()?.size===option.value);
          option.disabled=submitting||(index===api.sizeIndex(p)&&api.colorIndex(p)>=0&&!choices[api.colorIndex(p)])||!api.optionAvailable(p,index,option.value,choices);
        }),
      );
      const variant = choices.every(Boolean)
        ? p.variants.find((v) =>
            choices.every((choice, i) => choice === v.options[i]),
          )
        : null;
      if (selectionChanged) {
        for (const cardPhoto of state.dialog.querySelectorAll(".sw-format__card img[data-swish-finished-product]")) {
          if (cardPhoto.dataset.swishFinishedProduct === String(p.id)) {
            // Cleanup restores the prior provider photo, so retire it before
            // installing this exact variant's own fallback for the next attempt.
            finishedPhoto(cardPhoto, item, null);
            if (variant?.featured_image?.src) cardPhoto.src = variant.featured_image.src;
            if (variant) finishedPhoto(cardPhoto, item, variant);
          }
        }
      }
      id.value = variant?.id || "";
      submit.disabled = submitting||!variant?.available;
      submit.textContent = variant?.available
        ? state.l.add
        : state.l.select_options;
      feedback.textContent =
        variant && !variant.available ? state.l.unavailable : "";
      const productPhoto = panel.querySelector(":scope > img");
      // Revoke any prior finished selection before applying this selection's
      // honest provider photo, including when options become incomplete.
      if (productPhoto) finishedPhoto(productPhoto, item, null);
      if (productPhoto && variant?.featured_image?.src) productPhoto.src = variant.featured_image.src;
      if (productPhoto && variant) finishedPhoto(productPhoto, item, variant);
      if (variant) {
        price.textContent = money(variant.price);
        details.href = item.url + "?variant=" + variant.id;
      }
    }
    update();
    form.addEventListener("submit", async (event) => {
      event.preventDefault();
      if (submitting || submit.disabled || !id.value) return;
      submitting=true;update();
      submit.disabled = true;
      submit.textContent = state.l.adding;
      feedback.textContent = "";
      let added = false;
      try {
        const selectedId=id.value;
        const check=await fetch(item.url+'.js',{signal:AbortSignal.timeout(10000)});
        if(!check.ok)throw Error(api.labels.error);
        const fresh=await api.apply(await check.json());
        if(!fresh.variants.some(v=>String(v.id)===selectedId&&v.available))throw Error(api.labels.blocked);
        const body = new FormData(form);
        const drawer = document.querySelector("cart-drawer");
        const section = drawer
          ?.closest(".shopify-section")
          ?.id.replace("shopify-section-", "");
        body.set(
          "sections",
          ["cart-icon-bubble", section].filter(Boolean).join(","),
        );
        body.set("sections_url", location.pathname);
        const response = await fetch(
          (state.config.root || "/") + "cart/add.js",
          { method: "POST", body, headers: { Accept: "application/json" } },
        );
        const result = await response.json();
        if (!response.ok) {
          throw new Error(
            typeof result.description === "string"
              ? result.description
              : state.l.error,
          );
        }
        added = true;
        feedback.textContent = state.l.added;
        const icon = document.getElementById("cart-icon-bubble");
        if (icon && result.sections?.["cart-icon-bubble"])
          icon.innerHTML = result.sections["cart-icon-bubble"];
        const cartResponse = await fetch(
          (state.config.root || "/") + "cart.js",
        );
        const cart = cartResponse.ok ? await cartResponse.json() : null;
        if (cart) {
          document.dispatchEvent(
            new CustomEvent("cart:change", { detail: { cart } }),
          );
          form.dispatchEvent(
            new CustomEvent("on:cart:add", {
              bubbles: true,
              detail: { cart, variantId: Number(id.value) },
            }),
          );
        }
        state.dialog.close();
        if (drawer?.renderContents && result.sections) {
          await drawer.renderContents(result, true);
        } else location.href = (state.config.root || "/") + "cart";
      } catch (error) {
        feedback.textContent = added
          ? state.l.added + " · " + state.l.view_cart
          : error.message || state.l.error;
        if(!added)window.SwishMarketFailure(panel,async()=>{await api.apply(p);update();});
      } finally {
        submitting=false;update();
        submit.disabled = added||submit.disabled;
        submit.textContent = added ? state.l.added : state.l.add;
      }
    });
  }
  window.SwishFormatStudio = {
    estimateText,
    previewCards: () => previewCards,
    mount,
    empty() {
      const host = document.getElementById("swish-format-studio");
      if (host) {
        const label = window.SWISH_SHELF_CONFIG?.labels?.empty || "";
        host.querySelector(".sw-studio__status").textContent = label;
        host.querySelector(".sw-studio__open").hidden = true;
      }
    },
    async mapLimit(items, limit, mapper) {
      const results = new Array(items.length);
      let cursor = 0;
      await Promise.all(
        Array.from({ length: Math.min(limit, items.length) }, async () => {
          while (cursor < items.length) {
            const i = cursor++;
            try {
              results[i] = await mapper(items[i]);
            } catch {
              results[i] = null;
            }
          }
        }),
      );
      return results;
    },
  };
  document.addEventListener("keydown", (event) => {
    const confirmation = document.getElementById("swish-debut-confirm-modal");
    if (event.key === "Tab" && confirmation && confirmation.style.display !== "none") {
      const controls = Array.from(confirmation.querySelectorAll('button, a[href], input, select')).filter(n => !n.disabled && n.getClientRects().length);
      const first = controls[0], last = controls[controls.length - 1];
      if (event.shiftKey && document.activeElement === first) {event.preventDefault(); last?.focus();}
      else if (!event.shiftKey && document.activeElement === last) {event.preventDefault(); first?.focus();}
    }
    if (event.key === "Escape") {
      const modal = document.getElementById("swish-debut-confirm-modal");
      if (modal && modal.style.display !== "none")
        document.getElementById("swish-debut-confirm-cancel")?.click();
    }
  });
})();
